import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import LanguageSwitcher from '../components/LanguageSwitcher';

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: 44,
  background: 'var(--surface-alt)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '0 14px',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box',
};

export default function IndividualOnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [name, setName] = useState(user?.name ?? '');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [loading, setLoading] = useState(false);

  const timezones = Intl.supportedValuesOf('timeZone');

  const handleComplete = async () => {
    setLoading(true);
    try {
      await apiFetch('/tenants/me/onboarding-complete', { method: 'PATCH' });
      // #1807 — there is no /projects/new page: that URL matched
      // /projects/:projectId with projectId='new', and the dashboard fired
      // ~15 API calls with `new` as a project id. Open the New Project form
      // on the projects list instead.
      navigate('/projects?create=1', { replace: true });
    } catch {
      // Non-blocking — navigate anyway
      navigate('/projects', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div style={{
        width: '100%',
        maxWidth: 480,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-lg)',
        padding: 40,
      }}>
        {/* Progress indicator */}
        <div className="flex items-center justify-center mb-6" style={{ gap: 8 }}>
          {[1, 2].map((s) => (
            <div
              key={s}
              style={{
                width: 32,
                height: 4,
                borderRadius: 2,
                background: s <= step ? 'var(--primary)' : 'var(--border)',
                transition: 'background 200ms',
              }}
            />
          ))}
        </div>

        {step === 1 && (
          <>
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 22,
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: 8,
              textAlign: 'center',
            }}>
              {t('onboarding.individual.step1.title')}
            </h2>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: 14,
              color: 'var(--text-secondary)',
              textAlign: 'center',
              marginBottom: 28,
            }}>
              {t('onboarding.individual.step1.subtitle')}
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
                {t('onboarding.individual.step1.nameLabel')}
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                style={inputStyle}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
              />
            </div>

            <div style={{ marginBottom: 28 }}>
              <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
                {t('onboarding.individual.step1.timezoneLabel')}
              </label>
              <select
                value={timezone}
                onChange={e => setTimezone(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                {timezones.map(tz => (
                  <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>

            <button
              onClick={() => setStep(2)}
              disabled={!name.trim()}
              style={{
                display: 'block',
                width: '100%',
                height: 44,
                background: 'var(--primary)',
                color: 'white',
                fontFamily: 'var(--font-display)',
                fontSize: 15,
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                cursor: name.trim() ? 'pointer' : 'not-allowed',
                opacity: name.trim() ? 1 : 0.5,
              }}
            >
              {t('common.buttons.continue')}
            </button>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
              <LanguageSwitcher />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="flex flex-col items-center" style={{ marginBottom: 28 }}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'rgba(124, 58, 237, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>

              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 22,
                fontWeight: 700,
                color: 'var(--text)',
                marginBottom: 8,
              }}>
                {t('onboarding.individual.step2.title')}
              </h2>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: 14,
                color: 'var(--text-secondary)',
                textAlign: 'center',
              }}>
                {t('onboarding.individual.step2.subtitle')}
              </p>
            </div>

            <button
              onClick={handleComplete}
              disabled={loading}
              style={{
                display: 'block',
                width: '100%',
                height: 44,
                background: 'var(--primary)',
                color: 'white',
                fontFamily: 'var(--font-display)',
                fontSize: 15,
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? t('onboarding.individual.step2.settingUp') : t('onboarding.individual.step2.completeButton')}
            </button>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
              <LanguageSwitcher />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
